<template>
  <v-container fluid class="applicants-list">
    <v-card class="applicants-list__summary" elevation="0">
      <v-card-text>
        <div class="applicants-list__summary-header">
          <div class="applicants-list__summary-total">
            <div class="applicants-list__summary-label">Абитуриентов</div>
            <div class="applicants-list__summary-value">{{ stats.applicants }}</div>
          </div>

          <div class="applicants-list__summary-actions">
            <standard-log
              v-if="$model.isAdmin"
              class="applicants-list__deleted-log"
              target="createApplicant"
              objecttable="psm_adm_applicants"
              crud="d"
              title="Удаленные заявления"
            >
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  color="primary"
                  variant="tonal"
                  prepend-icon="mdi-delete-clock"
                >
                  Удаленные заявления
                </v-btn>
              </template>
            </standard-log>

            <v-btn
              color="primary"
              variant="flat"
              prepend-icon="mdi-account-plus"
              :href="createApplicantUrl"
              target="_blank"
            >
              Создать новое заявление
            </v-btn>
          </div>
        </div>

        <div class="applicants-list__study-stats">
          <div
            v-for="study in visibleStudyStats"
            :key="study.id"
            class="applicants-list__study-stat"
          >
            <div class="applicants-list__study-name">{{ study.name }}</div>
            <div class="applicants-list__study-values">
              <span>Заявлений: {{ study.applications }}</span>
              <span>Заявлений принято: {{ study.approvedApplications }}</span>
              <span>Мест: {{ study.seats }}</span>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-card class="applicants-list__filters" elevation="0">
      <v-card-actions class="applicants-list__filters-toggle">
        <v-btn
          variant="text"
          color="primary"
          :prepend-icon="showFilters ? 'mdi-chevron-up' : 'mdi-chevron-down'"
          @click="showFilters = !showFilters"
        >
          {{ showFilters ? 'Скрыть фильтры' : 'Показать фильтры' }}
        </v-btn>
      </v-card-actions>

      <v-expand-transition>
        <v-card-text v-show="showFilters" class="applicants-list__filters-body">
          <div class="applicants-list__filters-row">
            <v-autocomplete
              v-model="filters.department"
              autocomplete="one-time-code"
              :items="departments"
              item-title="name"
              item-value="id"
              label="Комплекс"
              variant="underlined"
              clearable
              density="compact"
              hide-details
            />

            <v-autocomplete
              v-model="filters.study"
              autocomplete="one-time-code"
              :items="studies"
              item-title="name"
              item-value="id"
              label="Форма обучения"
              variant="underlined"
              clearable
              density="compact"
              hide-details
            />

            <v-autocomplete
              v-model="filters.specialty"
              autocomplete="one-time-code"
              :items="specialties"
              item-title="title"
              item-value="id"
              label="Специальность"
              variant="underlined"
              clearable
              density="compact"
              hide-details
            />

            <v-select
              v-model="filters.financing"
              autocomplete="one-time-code"
              :items="financingTypes"
              item-title="name"
              item-value="id"
              label="Условия"
              variant="underlined"
              clearable
              density="compact"
              hide-details
            />

            <v-select
              v-model="filters.approved"
              autocomplete="one-time-code"
              :items="stateOptions"
              item-title="name"
              item-value="id"
              label="Утверждено"
              variant="underlined"
              clearable
              density="compact"
              hide-details
            />

            <v-select
              v-model="filters.educationBase"
              autocomplete="one-time-code"
              :items="educationBases"
              item-title="short_name"
              item-value="type_ed"
              label="База образования"
              variant="underlined"
              clearable
              density="compact"
              hide-details
            />

            <v-select
              v-model="filters.privilege"
              autocomplete="one-time-code"
              :items="privileges"
              item-title="name"
              item-value="id"
              label="Льготы"
              variant="underlined"
              clearable
              density="compact"
              hide-details
            />

            <v-checkbox
              v-model="filters.epgu"
              label="Подано через ЕПГУ"
              density="compact"
              hide-details
            />

            <v-checkbox
              v-model="filters.priorityOne"
              label="Приоритет 1"
              density="compact"
              hide-details
            />

            <v-checkbox
              v-model="filters.hasOriginals"
              label="Наличие оригиналов документов"
              density="compact"
              hide-details
            />

            <div class="applicants-list__date-filter">
              <div class="applicants-list__date-label">Дата подачи заявления</div>
              <div class="applicants-list__date-fields">
                <v-text-field
                  v-model="filters.fromDate"
                  autocomplete="one-time-code"
                  label="С"
                  type="date"
                  variant="underlined"
                  clearable
                  density="compact"
                  hide-details
                />
                <v-text-field
                  v-model="filters.toDate"
                  autocomplete="one-time-code"
                  label="По"
                  type="date"
                  variant="underlined"
                  clearable
                  density="compact"
                  hide-details
                />
              </div>
            </div>

            <div class="applicants-list__filter-actions">
              <v-btn color="primary" variant="flat" prepend-icon="mdi-filter" @click="reload">
                Применить
              </v-btn>
              <v-btn variant="text" prepend-icon="mdi-filter-remove" @click="resetFilters">
                Сбросить
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-expand-transition>
    </v-card>

    <v-data-table-server
      class="applicants-list__table"
      density="compact"
      :headers="headers"
      :items="items"
      :items-length="totalItems"
      :items-per-page-options="[15, 30, 50, 100]"
      :loading="loading"
      v-model:items-per-page="itemsPerPage"
      v-model:page="page"
      v-model:sort-by="sortBy"
      @update:options="getApplicants"
    >
      <template #header.fio>
        <div class="applicants-list__fio-header" @click.stop>
          <div class="applicants-list__fio-title">ФИО</div>
          <v-text-field
            v-model="filters.fio"
            autocomplete="one-time-code"
            class="applicants-list__fio-filter"
            density="compact"
            variant="underlined"
            clearable
            hide-details
            placeholder="Поиск"
            prepend-inner-icon="mdi-account-search"
            @click.stop
            @keydown.stop
            @keyup.enter="reload"
          />
        </div>
      </template>

      <template #item="{ item }">
        <tr>
          <td class="text-center">
            <v-icon :color="item.approved ? 'primary' : 'grey-lighten-1'">
              {{ item.approved ? 'mdi-check' : 'mdi-minus' }}
            </v-icon>
          </td>
          <td class="applicants-list__fio">
            <a :href="editApplicantUrl(item.id)" target="_blank">
              {{ item.fio || 'Без ФИО' }}
            </a>
            <v-tooltip v-if="item.hasValidationErrors" location="top">
              <template #activator="{ props }">
                <v-icon
                  v-bind="props"
                  class="applicants-list__validation-warning"
                  color="warning"
                  size="18"
                  icon="mdi-alert-circle"
                />
              </template>
              В заявлении присутствуют ошибки
            </v-tooltip>
          </td>
          <td v-for="study in studies" :key="'study-' + study.id" class="text-center">
            <v-icon :color="hasStudy(item, study.id) ? 'primary' : 'grey-lighten-1'">
              {{ hasStudy(item, study.id) ? 'mdi-check' : 'mdi-minus' }}
            </v-icon>
          </td>
          <td class="text-center">
            <v-icon :color="item.financing && item.financing.budget ? 'primary' : 'grey-lighten-1'">
              {{ item.financing && item.financing.budget ? 'mdi-check' : 'mdi-minus' }}
            </v-icon>
          </td>
          <td class="text-center">
            <v-icon :color="item.financing && item.financing.contract ? 'primary' : 'grey-lighten-1'">
              {{ item.financing && item.financing.contract ? 'mdi-check' : 'mdi-minus' }}
            </v-icon>
          </td>
          <td class="text-center">{{ item.age || '' }}</td>
          <td class="text-center">{{ item.lastApproved || 'Никогда' }}</td>
          <td class="text-center">{{ item.created || '' }}</td>
          <td class="text-center applicants-list__registration-number">
            {{ item.registrationNumber || '' }}
          </td>
          <td class="text-center applicants-list__actions">
            <v-menu>
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon="mdi-dots-horizontal"
                  variant="text"
                  density="compact"
                />
              </template>

              <v-list density="compact">
                <v-list-item
                  v-if="$model.isAdmin"
                  class="applicants-list__action-item"
                  prepend-icon="mdi-history"
                  title="Просмотреть лог"
                  @click="openApplicantLog(item)"
                />
                <v-list-item
                  class="applicants-list__action-item"
                  prepend-icon="mdi-printer"
                  title="Печать справки в школу"
                  :href="schoolCertificateUrl(item.personalId)"
                  target="_blank"
                  rel="noopener"
                />
                <v-list-item
                  class="applicants-list__action-item"
                  prepend-icon="mdi-lock-reset"
                  title="Восстановить пароль"
                  @click="openActionConfirm('restorePassword', item)"
                />
                <v-list-item
                  prepend-icon="mdi-delete"
                  title="Удалить заявление"
                  class="applicants-list__action-item applicants-list__danger-action"
                  @click="openActionConfirm('deleteApplicant', item)"
                />
              </v-list>
            </v-menu>
          </td>
        </tr>
      </template>
    </v-data-table-server>

    <v-dialog v-model="confirmDialog" max-width="460">
        <v-card>
          <v-card-title>{{ confirmTitle }}</v-card-title>
          <v-card-text>
          <template v-if="!actionResult">{{ confirmText }}</template>
          <div v-if="actionError" class="applicants-list__action-error">
            {{ actionError }}
          </div>
          <div v-if="actionResult" class="applicants-list__action-result">
            <template v-if="restoredPassword">
              <span class="applicants-list__password-label">Новый пароль:</span>
              <span class="applicants-list__password">{{ restoredPassword }}</span>
            </template>
            <template v-else>{{ actionResult }}</template>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="actionLoading" @click="confirmDialog = false">
            {{ actionResult ? 'Закрыть' : 'Отмена' }}
          </v-btn>
          <v-btn
            v-if="!actionResult"
            :color="pendingAction === 'deleteApplicant' ? 'error' : 'primary'"
            variant="flat"
            :loading="actionLoading"
            @click="confirmAction"
          >
            Подтвердить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <applicant-log-dialog
      v-if="$model.isAdmin"
      v-model="applicantLogDialog"
      :application-id="applicantLogId"
    />
  </v-container>
</template>

<script>
import ApplicantLogDialog from '../../components/ApplicantLogDialog.vue'
import StandardLog from 'moodleDir/procollege/lib/vue/log.vue'

export default {
  name: "applicantsList",
  components: {
    ApplicantLogDialog,
    StandardLog,
  },
  data() {
    return {
      loading: false,
      items: [],
      totalItems: 0,
      page: 1,
      itemsPerPage: 15,
      sortBy: [{ key: 'created', order: 'desc' }],
      confirmDialog: false,
      actionLoading: false,
      actionError: '',
      actionResult: '',
      restoredPassword: '',
      pendingAction: null,
      pendingApplicant: null,
      applicantLogDialog: false,
      applicantLogId: null,
      showFilters: false,
      departments: [],
      studies: [],
      specialties: [],
      educationBases: [],
      privileges: [],
      financingTypes: [
        { id: 'budget', name: 'Бюджет' },
        { id: 'contract', name: 'Договор' },
      ],
      stateOptions: [
        { id: '1', name: 'Да' },
        { id: '0', name: 'Нет' },
      ],
      stats: {
        applicants: 0,
        studies: [],
      },
      filters: {
        fio: '',
        department: null,
        study: null,
        specialty: null,
        financing: null,
        approved: null,
        educationBase: null,
        privilege: null,
        epgu: false,
        priorityOne: false,
        hasOriginals: false,
        fromDate: '',
        toDate: '',
      },
      abortControl: [],
      filterTimer: null,
    };
  },
  computed: {
    headers() {
      return [
        { title: '', key: 'approved', sortable: true, align: 'center', width: 44 },
        { title: 'ФИО', key: 'fio', sortable: true, width: 190 },
        ...this.studies.map((study) => ({
          title: study.name,
          key: 'study_' + study.id,
          sortable: false,
          align: 'center',
          width: 78,
        })),
        { title: 'Бюджет', key: 'budget', sortable: false, align: 'center', width: 72 },
        { title: 'Договор', key: 'contract', sortable: false, align: 'center', width: 72 },
        { title: 'Возраст', key: 'age', sortable: true, align: 'center', width: 64 },
        { title: 'Дата утверждения', key: 'lastApproved', sortable: true, align: 'center', width: 88 },
        { title: 'Дата подачи', key: 'created', sortable: true, align: 'center', width: 82 },
        { title: 'Рег. №', key: 'registrationNumber', sortable: false, align: 'center', width: 76 },
        { title: 'Действия', key: 'actions', sortable: false, align: 'center', width: 64 },
      ];
    },
    filtersSnapshot() {
      return JSON.stringify(this.filters);
    },
    visibleStudyStats() {
      return this.stats.studies.filter(study => study.applications || study.seats);
    },
    createApplicantUrl() {
      return this.$model.wwwroot + '/procollege/manage_groups/admission/createApplicant/index#/';
    },
    confirmTitle() {
      if (this.actionResult) {
        return 'Пароль восстановлен';
      }

      return this.pendingAction === 'deleteApplicant'
        ? 'Удалить заявление?'
        : 'Восстановить пароль?';
    },
    confirmText() {
      const fio = this.pendingApplicant && this.pendingApplicant.fio
        ? ` «${this.pendingApplicant.fio}»`
        : '';

      return this.pendingAction === 'deleteApplicant'
        ? `Подтвердите удаление заявления${fio}.`
        : `Подтвердите восстановление пароля заявления${fio}.`;
    },
  },
  watch: {
    filtersSnapshot() {
      clearTimeout(this.filterTimer);
      this.filterTimer = setTimeout(() => {
        this.reload();
      }, 350);
    },
  },
  beforeUnmount() {
    clearTimeout(this.filterTimer);
    this.abortRequests();
  },
  methods: {
    reload() {
      this.page = 1;
      this.getApplicants({
        page: this.page,
        itemsPerPage: this.itemsPerPage,
        sortBy: this.sortBy,
      });
    },
    resetFilters() {
      this.filters = {
        fio: '',
        department: null,
        study: null,
        specialty: null,
        financing: null,
        approved: null,
        educationBase: null,
        privilege: null,
        epgu: false,
        priorityOne: false,
        hasOriginals: false,
        fromDate: '',
        toDate: '',
      };
    },
    getApplicants({ page, itemsPerPage, sortBy }) {
      this.abortRequests();

      const abortControl = new AbortController();
      this.abortControl.push(abortControl);
      this.loading = true;

      this.axios
        .post(
          this.$model.wwwroot +
          "/procollege/manage_groups/admission/applicantsList/getApplicants",
          new URLSearchParams({
            page: page || this.page,
            perPage: itemsPerPage || this.itemsPerPage,
            sortBy: JSON.stringify(sortBy || this.sortBy),
            fio: this.filters.fio || '',
            department: this.filters.department || '',
            study: this.filters.study || '',
            specialty: this.filters.specialty || '',
            financing: this.filters.financing || '',
            approved: this.filters.approved || '',
            educationBase: this.filters.educationBase !== null && this.filters.educationBase !== undefined
              ? this.filters.educationBase
              : '',
            privilege: this.filters.privilege || '',
            epgu: this.filters.epgu ? '1' : '',
            priorityOne: this.filters.priorityOne ? '1' : '',
            hasOriginals: this.filters.hasOriginals ? '1' : '',
            fromDate: this.filters.fromDate || '',
            toDate: this.filters.toDate || '',
          }),
          {
            signal: abortControl.signal,
          }
        )
        .then((response) => {
          const data = response.data || {};

          this.departments = Array.isArray(data.departments) ? data.departments : this.departments;
          this.studies = Array.isArray(data.studies) ? data.studies : this.studies;
          this.specialties = Array.isArray(data.specialties) ? data.specialties : this.specialties;
          this.educationBases = Array.isArray(data.educationBases) ? data.educationBases : this.educationBases;
          this.privileges = Array.isArray(data.privileges) ? data.privileges : this.privileges;
          this.financingTypes = Array.isArray(data.financingTypes) ? data.financingTypes : this.financingTypes;
          this.stats = data.stats && typeof data.stats === 'object'
            ? {
                applicants: Number(data.stats.applicants || 0),
                studies: Array.isArray(data.stats.studies) ? data.stats.studies : [],
              }
            : { applicants: 0, studies: [] };
          this.items = Array.isArray(data.rows) ? data.rows : [];
          this.totalItems = Number(data.totalItems || 0);
        })
        .catch((error) => {
          if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
            return;
          }

          if (this.axiosDefaultErrorHandler) {
            this.axiosDefaultErrorHandler(error);
          } else {
            console.log(error);
          }
        })
        .finally(() => {
          this.abortControl = this.abortControl.filter(item => item !== abortControl);
          this.loading = false;
        });
    },
    abortRequests() {
      let abortControl;
      while ((abortControl = this.abortControl.pop())) {
        abortControl.abort();
      }
    },
    hasStudy(item, studyId) {
      return Boolean(item.studies && item.studies[studyId]);
    },
    openApplicantLog(applicant) {
      if (!this.$model.isAdmin || !applicant || !applicant.id) {
        return;
      }

      this.applicantLogId = applicant.id;
      this.applicantLogDialog = true;
    },
    openActionConfirm(action, applicant) {
      this.pendingAction = action;
      this.pendingApplicant = applicant;
      this.actionError = '';
      this.actionResult = '';
      this.restoredPassword = '';
      this.confirmDialog = true;
    },
    confirmAction() {
      if (!this.pendingAction || !this.pendingApplicant || this.actionLoading) {
        return;
      }

      this.actionLoading = true;
      this.actionError = '';

      this.axios
        .post(
          this.$model.wwwroot +
          "/procollege/manage_groups/admission/applicantsList/" +
          this.pendingAction,
          new URLSearchParams({
            id: this.pendingApplicant.id,
          })
        )
        .then((response) => {
          const data = response.data || {};
          const errors = Array.isArray(data.errors) ? data.errors : [];

          if (errors.length) {
            this.actionError = errors.join(' ');
            return;
          }

          if (this.pendingAction === 'restorePassword') {
            this.restoredPassword = data.password || '';
            this.actionResult = data.password
              ? `Новый пароль: ${data.password}`
              : 'Пароль восстановлен.';
            return;
          }

          this.confirmDialog = false;

          this.reload();
        })
        .catch((error) => {
          const data = error.response && error.response.data
            ? error.response.data
            : {};

          this.actionError = Array.isArray(data.errors) && data.errors.length
            ? data.errors.join(' ')
            : 'Не удалось выполнить действие';
        })
        .finally(() => {
          this.actionLoading = false;
        });
    },
    editApplicantUrl(applicantId) {
      return this.$model.wwwroot +
        '/procollege/manage_groups/admission/createApplicant/index#/' +
        applicantId;
    },
    schoolCertificateUrl(personalId) {
      return this.$model.wwwroot +
        '/blocks/manage_groups/admission_new/print_school.php?id=' +
        personalId;
    },
  },
};
</script>

<style scoped>
.applicants-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-right: 8px;
  padding-left: 8px;
}

.applicants-list__filters {
  border-radius: 8px;
}

.applicants-list__filters-toggle {
  min-height: 40px;
  padding: 0;
}

.applicants-list__filters-body {
  padding: 0 0 8px;
}

.applicants-list__filters-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 420px));
  gap: 12px 18px;
  align-items: end;
}

.applicants-list__filters-row > :deep(.v-input),
.applicants-list__date-filter {
  width: 100%;
}

.applicants-list__date-filter {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.applicants-list__date-label {
  color: #667085;
  font-size: 12px;
  line-height: 1;
}

.applicants-list__date-fields {
  display: grid;
  grid-template-columns: minmax(130px, 1fr) minmax(130px, 1fr);
  gap: 8px;
}

.applicants-list__date-filter {
  min-width: 0;
  grid-column: 1 / -1;
}

.applicants-list__date-fields :deep(input[type="date"]) {
  position: relative;
  padding-right: 34px;
}

.applicants-list__date-fields :deep(input[type="date"]::-webkit-calendar-picker-indicator) {
  position: absolute;
  right: 0;
  width: 20px;
  height: 20px;
  margin: 0;
  cursor: pointer;
}

.applicants-list__summary {
  border-radius: 8px;
}

.applicants-list__summary-header {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
}

.applicants-list__summary-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
}

.applicants-list__summary-actions :deep(.v-btn) {
  cursor: pointer;
}

.applicants-list__deleted-log {
  width: auto;
  margin: 0;
  padding: 0;
}

.applicants-list__summary-total {
  display: flex;
  gap: 10px;
  align-items: baseline;
}

.applicants-list__summary-label {
  color: #667085;
  font-size: 13px;
}

.applicants-list__summary-value {
  color: #1f2937;
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
}

.applicants-list__study-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px;
  margin-top: 14px;
}

.applicants-list__study-stat {
  min-width: 0;
  padding: 8px 10px;
  background: #f8fafc;
  border: 1px solid #e6e8ee;
  border-radius: 6px;
}

.applicants-list__study-name {
  margin-bottom: 4px;
  color: #344054;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.applicants-list__study-values {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  color: #667085;
  font-size: 12px;
  line-height: 1.2;
}

.applicants-list__filter-actions {
  display: flex;
  gap: 8px;
  grid-column: 1 / -1;
  align-items: center;
}

@media (max-width: 860px) {
  .applicants-list__summary-header {
    align-items: flex-start;
  }

  .applicants-list__summary-actions {
    justify-content: flex-start;
  }

  .applicants-list__filters-row {
    grid-template-columns: minmax(0, 1fr);
  }
}

.applicants-list__table {
  border: 1px solid #e6e8ee;
  border-radius: 8px;
}

.applicants-list__table :deep(table) {
  table-layout: fixed;
}

.applicants-list__table :deep(th) {
  height: 70px;
  padding: 0 6px !important;
  vertical-align: middle;
  white-space: normal;
}

.applicants-list__table :deep(.v-data-table-header__content) {
  justify-content: center;
  min-width: 0;
  font-size: 12px;
  line-height: 1.15;
  text-align: center;
  white-space: normal;
  overflow-wrap: anywhere;
}

.applicants-list__table :deep(td) {
  height: 40px;
  padding: 0 6px !important;
  vertical-align: middle;
}

.applicants-list__fio {
  width: 190px;
  font-weight: 600;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.applicants-list__fio-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: stretch;
  width: 100%;
  min-width: 0;
  padding-top: 4px;
}

.applicants-list__fio-title {
  font-size: 12px;
  font-weight: 700;
  line-height: 1.1;
  text-align: center;
}

.applicants-list__fio-filter {
  width: 100%;
}

.applicants-list__fio-filter :deep(.v-field) {
  font-size: 12px;
}

.applicants-list__fio-filter :deep(.v-field__input) {
  min-height: 24px;
  padding-top: 0;
  padding-bottom: 0;
}

.applicants-list__fio a {
  color: #1d4ed8;
  cursor: pointer;
  text-decoration: none;
}

.applicants-list__fio a:hover {
  text-decoration: underline;
}

.applicants-list__validation-warning {
  margin-left: 4px;
  vertical-align: text-bottom;
  cursor: help;
}

.applicants-list__registration-number {
  width: 76px;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.applicants-list__actions {
  width: 64px;
}

.applicants-list__actions :deep(.v-btn),
.applicants-list__action-item {
  cursor: pointer;
}

.applicants-list__danger-action {
  color: #b42318;
}

.applicants-list__action-error {
  margin-top: 12px;
  color: #b42318;
  font-size: 14px;
}

.applicants-list__action-result {
  margin-top: 16px;
  font-weight: 600;
  white-space: pre-wrap;
}

.applicants-list__password-label {
  margin-right: 4px;
  user-select: none;
}

.applicants-list__password {
  cursor: text;
  user-select: text;
}
</style>
